import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import TrialUpgradeModal from '../components/TrialUpgradeModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { comparisonsAPI, foldersAPI } from '../lib/api';
import { 
  Plus, Layers, MoreVertical, Trash2, ExternalLink, 
  Loader2, FileText, CheckCircle, Clock, AlertCircle,
  Lock, Folder, FolderPlus, FolderOpen, ChevronRight
} from 'lucide-react';
import Footer from '../components/Footer';

const SmartRevisionPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newComparison, setNewComparison] = useState({ name: '', description: '', folder_id: null });
  
  // Folders state
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  
  // Trial upgrade modal state
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialModalType, setTrialModalType] = useState('trial_comparison_limit');
  
  const plan = user?.plan?.toUpperCase() || 'FREE';
  const isAI = plan === 'AI' || plan === 'AI_UNLIMITED' || plan === 'AI_TRIAL';
  const aiCredits = user?.ai_usage_count || 0;
  const aiLimit = plan === 'AI' ? 50 : (plan === 'AI_UNLIMITED' || plan === 'AI_TRIAL' ? 999999 : 0);
  
  useEffect(() => {
    // Refresh user data to ensure we have the latest plan
    if (refreshUser) {
      refreshUser();
    }
  }, []);
  
  useEffect(() => {
    if (user && isAI) {
      loadComparisons();
      loadFolders();
    } else if (user && !isAI) {
      setLoading(false);
    }
  }, [user, isAI]);
  
  const loadFolders = async () => {
    try {
      // Load only 'comparisons' type folders for SmartRevision
      const response = await foldersAPI.list('comparisons');
      setFolders(response.data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };
  
  const loadComparisons = async () => {
    try {
      setLoading(true);
      const response = await comparisonsAPI.list();
      const comps = response.data?.comparisons || response?.comparisons || [];
      setComparisons(comps);
    } catch (error) {
      console.error('Error loading comparisons:', error);
      toast.error('Errore nel caricamento delle comparazioni');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newComparison.name.trim()) return;
    
    setCreating(true);
    try {
      const response = await comparisonsAPI.create(newComparison);
      setCreateOpen(false);
      setNewComparison({ name: '', description: '', folder_id: null });
      toast.success('Comparazione creata');
      navigate(`/compare/${response.data.id}`);
    } catch (error) {
      // Handle trial limit errors
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === 'trial_comparison_limit') {
        setCreateOpen(false);
        setTrialModalType('trial_comparison_limit');
        setTrialModalOpen(true);
      } else if (errorDetail === 'trial_expired') {
        setCreateOpen(false);
        setTrialModalType('trial_expired');
        setTrialModalOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Errore nella creazione');
      }
    } finally {
      setCreating(false);
    }
  };
  
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setCreatingFolder(true);
    try {
      // Create folder with type 'comparisons' for SmartRevision
      await foldersAPI.create(newFolderName.trim(), 'comparisons');
      setCreateFolderOpen(false);
      setNewFolderName('');
      loadFolders();
      toast.success('Cartella creata');
    } catch (error) {
      toast.error('Errore nella creazione della cartella');
    } finally {
      setCreatingFolder(false);
    }
  };
  
  const handleMoveToFolder = async (compId, folderId) => {
    try {
      await comparisonsAPI.moveToFolder(compId, folderId);
      loadComparisons();
      toast.success(folderId ? 'Spostato nella cartella' : 'Rimosso dalla cartella');
    } catch (error) {
      toast.error('Errore nello spostamento');
    }
  };
  
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };
  
  const getComparisonsInFolder = (folderId) => {
    return comparisons.filter(c => c.folder_id === folderId);
  };
  
  const getComparisonsWithoutFolder = () => {
    return comparisons.filter(c => !c.folder_id);
  };
  
  const handleDelete = async (compId, e) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questa comparazione?')) return;
    
    try {
      await comparisonsAPI.delete(compId);
      setComparisons(comparisons.filter(c => c.id !== compId));
      toast.success('Comparazione eliminata');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            {t('status_completed')}
          </span>
        );
      case 'analyzing':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            {t('status_analyzing')}
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
            <AlertCircle className="w-3 h-3" />
            {t('status_failed')}
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
            <Clock className="w-3 h-3" />
            {t('status_pending')}
          </span>
        );
    }
  };
  
  // ComparisonCard sub-component
  const ComparisonCard = ({ comp, folders, onNavigate, onDelete, onMoveToFolder, getStatusBadge }) => (
    <Card 
      className="hover:border-purple-300 transition-colors cursor-pointer group"
      onClick={onNavigate}
      data-testid={`comparison-card-${comp.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="w-4 h-4 text-purple-500" />
            <span className="truncate max-w-[180px]">{comp.name}</span>
          </CardTitle>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreVertical className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onNavigate}>
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('open_comparison')}
              </DropdownMenuItem>
              
              {/* Move to folder submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Folder className="w-4 h-4 mr-2" />
                  {t('move_to_folder')}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {comp.folder_id && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMoveToFolder(comp.id, null); }}>
                      <span className="text-slate-500">{t('remove_from_folder')}</span>
                    </DropdownMenuItem>
                  )}
                  {folders.map(f => (
                    <DropdownMenuItem 
                      key={f.id} 
                      onClick={(e) => { e.stopPropagation(); onMoveToFolder(comp.id, f.id); }}
                      disabled={comp.folder_id === f.id}
                    >
                      {f.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-red-600"
                onClick={(e) => onDelete(comp.id, e)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardDescription className="truncate">
          {comp.has_drawing_a && comp.has_drawing_b 
            ? `${comp.drawing_a_name || 'A'} ${t('vs')} ${comp.drawing_b_name || 'B'}`
            : comp.description || ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          {getStatusBadge(comp.analysis_status)}
          <Button 
            size="sm" 
            variant="outline"
            className="border-purple-200 text-purple-700 hover:bg-purple-50"
            onClick={(e) => { e.stopPropagation(); onNavigate(); }}
          >
            {t('open_comparison')}
          </Button>
        </div>
        {comp.credits_used > 0 && (
          <p className="text-xs text-slate-400 mt-2">
            {t('credits_used')} {comp.credits_used}
          </p>
        )}
      </CardContent>
    </Card>
  );
  
  // Non-AI users see upgrade prompt
  if (!isAI) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">{t('smartrevision_page_title')}</h1>
            <p className="text-lg text-slate-600 mb-2">{t('smartrevision_page_subtitle')}</p>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">
              {t('smartrevision_subtitle')}
            </p>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-8 max-w-md mx-auto">
              <h3 className="font-semibold text-purple-900 mb-4">{t('features_included')}</h3>
              <ul className="text-left text-purple-800 space-y-2 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-600" />
                  {t('visual_comparison')}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-600" />
                  {t('semantic_analysis')}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-600" />
                  {t('pdf_export_report')}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-600" />
                  {t('save_comparisons')}
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/subscription')}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {t('upgrade_to_ai')}
              </Button>
              <p className="text-xs text-purple-600 mt-3">
                {t('starting_from')}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Layers className="w-7 h-7 text-purple-600" />
              {t('smartrevision_page_title')}
            </h1>
            <p className="text-slate-500 mt-1">{t('smartrevision_page_subtitle')}</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* AI Credits */}
            {plan === 'AI' && (
              <div className="text-sm text-slate-600">
                {t('credits_label')} <span className="font-medium">{aiLimit - aiCredits}</span> / {aiLimit}
              </div>
            )}
            
            {/* New Comparison Button */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700" data-testid="new-comparison-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('new_comparison')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-purple-600" />
                    {t('new_comparison')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('smartrevision_subtitle')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>{t('comparison_name')}</Label>
                    <Input
                      value={newComparison.name}
                      onChange={(e) => setNewComparison({ ...newComparison, name: e.target.value })}
                      placeholder="Es: Parte XYZ - Rev A vs Rev B"
                      required
                      data-testid="comparison-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('comparison_description')}</Label>
                    <Textarea
                      value={newComparison.description}
                      onChange={(e) => setNewComparison({ ...newComparison, description: e.target.value })}
                      placeholder="..."
                      rows={2}
                    />
                  </div>
                  {folders.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t('folder_name')}</Label>
                      <select
                        value={newComparison.folder_id || ''}
                        onChange={(e) => setNewComparison({ ...newComparison, folder_id: e.target.value || null })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                      >
                        <option value="">-</option>
                        {folders.map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                    <strong>Note:</strong> {t('ai_credits_50')}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={creating}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {creating ? t('loading') : t('create_comparison')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Comparisons List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : comparisons.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Layers className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">{t('no_comparisons')}</h3>
            <p className="text-slate-500 mb-6">
              {t('no_comparisons_desc')}
            </p>
            <Button 
              onClick={() => setCreateOpen(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('new_comparison')}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Create Folder Button */}
            <div className="flex items-center gap-2">
              <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FolderPlus className="w-4 h-4 mr-2" />
                    {t('new_folder')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('new_folder')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Input
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder={t('folder_name')}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>{t('cancel')}</Button>
                      <Button onClick={handleCreateFolder} disabled={creatingFolder}>
                        {creatingFolder ? t('loading') : t('create_folder')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Folders - show all folders, even empty ones */}
            {folders.map(folder => {
              const folderComparisons = getComparisonsInFolder(folder.id);
              const isExpanded = expandedFolders.has(folder.id);
              
              return (
                <div key={folder.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFolder(folder.id)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <FolderOpen className="w-5 h-5 text-purple-600" />
                      ) : (
                        <Folder className="w-5 h-5 text-purple-600" />
                      )}
                      <span className="font-medium text-slate-700">{folder.name}</span>
                      <span className="text-sm text-slate-500">({folderComparisons.length})</span>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {isExpanded && (
                    <div className="p-4 bg-white">
                      {folderComparisons.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Cartella vuota - sposta qui le comparazioni</p>
                      ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {folderComparisons.map(comp => (
                            <ComparisonCard 
                              key={comp.id} 
                              comp={comp} 
                              folders={folders}
                              onNavigate={() => navigate(`/compare/${comp.id}`)}
                              onDelete={handleDelete}
                              onMoveToFolder={handleMoveToFolder}
                              getStatusBadge={getStatusBadge}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Comparisons without folder */}
            {getComparisonsWithoutFolder().length > 0 && (
              <div>
                {folders.some(f => getComparisonsInFolder(f.id).length > 0) && (
                  <h3 className="text-sm font-medium text-slate-500 mb-3">{t('no_folder')}</h3>
                )}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {getComparisonsWithoutFolder().map(comp => (
                    <ComparisonCard 
                      key={comp.id} 
                      comp={comp} 
                      folders={folders}
                      onNavigate={() => navigate(`/compare/${comp.id}`)}
                      onDelete={handleDelete}
                      onMoveToFolder={handleMoveToFolder}
                      getStatusBadge={getStatusBadge}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Footer with Terms/Privacy links */}
      <Footer />
      
      {/* Trial Upgrade Modal */}
      <TrialUpgradeModal 
        isOpen={trialModalOpen}
        onClose={() => setTrialModalOpen(false)}
        type={trialModalType}
      />
    </div>
  );
};

export default SmartRevisionPage;
